/**
 * Pricing page: Free vs Pro plans, competitor comparison, Stripe Checkout.
 * Pro users get a calm plan view with manage billing + cancel-at-period-end.
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Seo from '../components/Seo';
import { startStripeCheckout } from '../utils/checkout';
import { REGISTER_TO } from '../utils/paths';
import './Pricing.css';

const formatPeriodEnd = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-GB' : 'es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const CANCEL_REASON_KEYS = [
  { id: 'too_expensive', labelKey: 'pricing.cancelReasonTooExpensive' },
  { id: 'missing_features', labelKey: 'pricing.cancelReasonMissingFeatures' },
  { id: 'switched_service', labelKey: 'pricing.cancelReasonSwitched' },
  { id: 'unused', labelKey: 'pricing.cancelReasonUnused' },
  { id: 'customer_service', labelKey: 'pricing.cancelReasonSupport' },
  { id: 'too_complex', labelKey: 'pricing.cancelReasonComplex' },
  { id: 'low_quality', labelKey: 'pricing.cancelReasonQuality' },
  { id: 'other', labelKey: 'pricing.cancelReasonOther' },
];

const Pricing = () => {
  const {
    isAuthenticated,
    isPro,
    cancelAtPeriodEnd,
    currentPeriodEnd,
    refreshSubscription,
  } = useAuth();
  const { t, lang, localePath } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [oneshotLoading, setOneshotLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelComment, setCancelComment] = useState('');
  const cancelKeepRef = useRef(null);

  const openCancelModal = () => {
    setCancelReason('');
    setCancelComment('');
    setShowCancelConfirm(true);
  };

  const closeCancelModal = () => {
    if (cancelLoading) return;
    setShowCancelConfirm(false);
    setCancelReason('');
    setCancelComment('');
  };

  useEffect(() => {
    if (!showCancelConfirm) return undefined;
    cancelKeepRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !cancelLoading) {
        closeCancelModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showCancelConfirm, cancelLoading]);

  const periodLabel = formatPeriodEnd(currentPeriodEnd, lang);

  const handleUpgrade = async () => {
    if (!isAuthenticated) {
      navigate(REGISTER_TO);
      return;
    }
    if (isPro) {
      navigate('/dashboard');
      return;
    }
    setLoading(true);
    try {
      await startStripeCheckout({ kind: 'pro_intro', returnPath: '/pricing' });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('pricing.checkoutError');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleOneshot = async () => {
    if (!isAuthenticated) {
      navigate(REGISTER_TO);
      return;
    }
    if (isPro) {
      navigate('/dashboard');
      return;
    }
    setOneshotLoading(true);
    try {
      await startStripeCheckout({
        kind: 'oneshot_pdf',
        returnPath: '/dashboard/edit-cv',
      });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('pricing.checkoutError');
      toast.error(msg);
    } finally {
      setOneshotLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      const { data } = await axios.post('/api/cvs/create-billing-portal-session/');
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error(t('pricing.portalError'));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('pricing.portalError');
      toast.error(msg);
    } finally {
      setPortalLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason) {
      toast.error(t('pricing.cancelReasonRequired'));
      return;
    }
    if (cancelReason === 'other' && cancelComment.trim().length < 3) {
      toast.error(t('pricing.cancelCommentOther'));
      return;
    }
    setCancelLoading(true);
    try {
      const { data } = await axios.post('/api/cvs/cancel-subscription/', {
        reason: cancelReason,
        comment: cancelComment.trim(),
      });
      if (data?.subscription) {
        await refreshSubscription();
      }
      closeCancelModal();
      toast.success(t('pricing.cancelScheduled'));
    } catch (err) {
      const msg = err.response?.data?.error || err.message || t('pricing.cancelError');
      toast.error(msg);
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className={`pricing-page${isPro ? ' pricing-page--subscribed' : ''}`}>
      <Seo
        title={isPro ? t('pricing.titlePro') : t('pricing.title')}
        description={isPro ? t('pricing.leadPro') : t('pricing.lead')}
        path="/pricing"
        lang={lang}
        hreflang
      />
      <div className="container">
        <header className="pricing-page__header">
          {isPro && (
            <p className="pricing-page__eyebrow">
              {cancelAtPeriodEnd ? t('pricing.statusEnding') : t('pricing.statusActive')}
            </p>
          )}
          <h1>{isPro ? t('pricing.titlePro') : t('pricing.title')}</h1>
          <p>
            {isPro
              ? cancelAtPeriodEnd && periodLabel
                ? t('pricing.leadCanceling', { date: periodLabel })
                : t('pricing.leadPro')
              : t('pricing.lead')}
          </p>
        </header>

        <div className={`pricing-page__grid${isPro ? ' pricing-page__grid--subscribed' : ''}`}>
          <article
            className={`pricing-card${isPro ? ' pricing-card--muted' : ''}${
              !isPro && isAuthenticated ? ' pricing-card--current' : ''
            }${!isPro && !isAuthenticated ? ' pricing-card--entry' : ''}`}
          >
            <div className="pricing-card__top">
              <h2>{t('pricing.free')}</h2>
              {!isPro && (
                <span className="pricing-card__status">
                  {isAuthenticated ? t('pricing.yourPlan') : t('pricing.startEyebrow')}
                </span>
              )}
            </div>
            <p className="pricing-card__price">
              0 €<span>{t('pricing.perMonth')}</span>
            </p>
            <ul>
              <li>{t('pricing.free1')}</li>
              <li>{t('pricing.free2')}</li>
              <li>{t('pricing.free3')}</li>
              <li>{t('pricing.free4')}</li>
              <li>{t('pricing.free5')}</li>
              <li>{t('pricing.free6')}</li>
            </ul>
            {!isAuthenticated && (
              <Link to={REGISTER_TO} className="btn btn-primary">
                {t('pricing.startFree')}
              </Link>
            )}
          </article>

          <article
            className={`pricing-card pricing-card--pro${isPro ? ' pricing-card--active' : ''}`}
          >
            <div className="pricing-card__top">
              <h2>{t('pricing.pro')}</h2>
              <span className="pricing-card__status">
                {isPro ? t('pricing.yourPlan') : t('pricing.recommended')}
              </span>
            </div>
            <p className="pricing-card__price">
              1,95 €<span>{t('pricing.introPeriod')}</span>
            </p>
            <p className="pricing-card__then">{t('pricing.thenMonthly')}</p>
            <ul>
              <li>{t('pricing.pro1')}</li>
              <li>{t('pricing.pro2')}</li>
              <li>{t('pricing.pro3')}</li>
              <li>{t('pricing.pro4')}</li>
              <li>{t('pricing.pro5')}</li>
            </ul>

            {isPro ? (
              <div className="pricing-card__actions">
                <Link to="/dashboard" className="btn btn-primary">
                  {t('pricing.goDashboard')}
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleManageBilling}
                  disabled={portalLoading}
                >
                  {portalLoading ? t('pricing.portalLoading') : t('pricing.manageBilling')}
                </button>
                {!cancelAtPeriodEnd ? (
                  <button
                    type="button"
                    className="pricing-card__cancel-link"
                    onClick={openCancelModal}
                  >
                    {t('pricing.cancelSubscription')}
                  </button>
                ) : (
                  <p className="pricing-card__footnote">
                    {periodLabel
                      ? t('pricing.cancelPending', { date: periodLabel })
                      : t('pricing.cancelPendingShort')}
                  </p>
                )}
              </div>
            ) : (
              <div className="pricing-card__actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpgrade}
                  disabled={loading || oneshotLoading}
                >
                  {loading ? t('pricing.upgrading') : t('pricing.upgrade')}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleOneshot}
                  disabled={loading || oneshotLoading}
                >
                  {oneshotLoading ? t('pricing.upgrading') : t('pricing.oneshotCta')}
                </button>
                <p className="pricing-card__footnote">{t('pricing.upgradeHint')}</p>
              </div>
            )}

            {isPro && !cancelAtPeriodEnd && (
              <p className="pricing-card__footnote">{t('pricing.proFootnote')}</p>
            )}
          </article>
        </div>

        {!isPro && (
          <section className="pricing-interview" aria-labelledby="pricing-interview-title">
            <div className="pricing-interview__copy">
              <h2 id="pricing-interview-title">{t('pricing.interviewTitle')}</h2>
              <p>{t('pricing.interviewLead')}</p>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpgrade}
              disabled={loading}
            >
              {loading ? t('pricing.upgrading') : t('pricing.interviewCta')}
            </button>
          </section>
        )}

        {!isPro && (
          <section className="pricing-compare" aria-labelledby="pricing-compare-title">
            <h2 id="pricing-compare-title" className="pricing-compare__title">
              {t('pricing.compareTitle')}
            </h2>
            <p className="pricing-compare__lead">{t('pricing.compareLead')}</p>

            <div className="pricing-compare__scroll">
              <table className="pricing-compare__table">
                <caption className="pricing-compare__caption">{t('pricing.compareCaption')}</caption>
                <thead>
                  <tr>
                    <th scope="col"> </th>
                    <th scope="col" className="pricing-compare__us">
                      ClarCV
                    </th>
                    <th scope="col">Kickresume</th>
                    <th scope="col">Rezi / Teal</th>
                    <th scope="col">{t('pricing.typicalEs')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row">{t('pricing.rowStartFree')}</th>
                    <td className="pricing-compare__us">
                      <strong>{t('pricing.yesFree')}</strong>
                    </td>
                    <td>{t('pricing.freeLimited')}</td>
                    <td>{t('pricing.freeLimited')}</td>
                    <td>{t('pricing.frequent')}</td>
                  </tr>
                  <tr>
                    <th scope="row">{t('pricing.rowPrice')}</th>
                    <td className="pricing-compare__us">
                      <strong>0 € · 1,95–7 €</strong>
                    </td>
                    <td>~24 $</td>
                    <td>~29 $</td>
                    <td>~15–25 €</td>
                  </tr>
                  <tr>
                    <th scope="row">{t('pricing.rowImprove')}</th>
                    <td className="pricing-compare__us">{t('pricing.yesPdf')}</td>
                    <td>{t('pricing.partial')}</td>
                    <td>{t('pricing.yes')}</td>
                    <td>{t('pricing.oftenEditor')}</td>
                  </tr>
                  <tr>
                    <th scope="row">{t('pricing.rowTrial')}</th>
                    <td className="pricing-compare__us">{t('pricing.paywallHonest')}</td>
                    <td>{t('pricing.freeLimited')}</td>
                    <td>{t('pricing.freeLimited')}</td>
                    <td>{t('pricing.frequent')}</td>
                  </tr>
                  <tr>
                    <th scope="row">{t('pricing.rowUi')}</th>
                    <td className="pricing-compare__us">{t('pricing.yes')}</td>
                    <td>{t('pricing.yes')}</td>
                    <td>{t('pricing.mainlyEn')}</td>
                    <td>{t('pricing.yes')}</td>
                  </tr>
                  <tr>
                    <th scope="row">{t('pricing.rowOut')}</th>
                    <td className="pricing-compare__us">{t('pricing.yes')}</td>
                    <td>{t('pricing.yes')}</td>
                    <td>{t('pricing.yes')}</td>
                    <td>{t('pricing.yes')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="pricing-compare__footnote">{t('pricing.footnote')}</p>
          </section>
        )}

        <p className="pricing-page__note">
          {t('pricing.noteBefore')} <Link to={localePath('/privacy')}>{t('pricing.privacy')}</Link>{' '}
          {t('pricing.noteAnd')} <Link to={localePath('/terms')}>{t('pricing.terms')}</Link>
          {t('pricing.noteEnd')}
        </p>
      </div>

      {showCancelConfirm && (
        <div
          className="pricing-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pricing-cancel-title"
        >
          <button
            type="button"
            className="pricing-modal__backdrop"
            aria-label={t('pricing.cancelDismiss')}
            onClick={closeCancelModal}
          />
          <div className="pricing-modal__panel">
            <h2 id="pricing-cancel-title">{t('pricing.cancelTitle')}</h2>
            <p>
              {periodLabel
                ? t('pricing.cancelBody', { date: periodLabel })
                : t('pricing.cancelBodyShort')}
            </p>

            <fieldset className="pricing-cancel-feedback">
              <legend className="pricing-cancel-feedback__legend">
                {t('pricing.cancelFeedbackTitle')}
              </legend>
              <div className="pricing-cancel-feedback__options" role="radiogroup">
                {CANCEL_REASON_KEYS.map((item) => (
                  <label key={item.id} className="pricing-cancel-feedback__option">
                    <input
                      type="radio"
                      name="cancel-reason"
                      value={item.id}
                      checked={cancelReason === item.id}
                      disabled={cancelLoading}
                      onChange={() => setCancelReason(item.id)}
                    />
                    <span>{t(item.labelKey)}</span>
                  </label>
                ))}
              </div>
              <label className="pricing-cancel-feedback__comment" htmlFor="cancel-comment">
                {cancelReason === 'other'
                  ? t('pricing.cancelCommentOther')
                  : t('pricing.cancelCommentLabel')}
              </label>
              <textarea
                id="cancel-comment"
                className="pricing-cancel-feedback__textarea"
                rows={3}
                maxLength={1000}
                disabled={cancelLoading}
                value={cancelComment}
                placeholder={t('pricing.cancelCommentPlaceholder')}
                onChange={(e) => setCancelComment(e.target.value)}
              />
            </fieldset>

            <div className="pricing-modal__actions">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={cancelLoading}
                ref={cancelKeepRef}
                onClick={closeCancelModal}
              >
                {t('pricing.cancelKeep')}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={cancelLoading || !cancelReason}
                onClick={handleConfirmCancel}
              >
                {cancelLoading ? t('pricing.canceling') : t('pricing.cancelConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
